<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500"
                                        @click="goBack"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Edit 2D Model</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">2D Model ID: {{ routeId }}</p>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="loading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading 2D model...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="p-6">
                                        <h3 class="text-base font-semibold text-gray-900 m-0 mb-4">2D Model Properties</h3>
                                        <div class="max-w-lg">
                                                <!-- Name Field -->
                                                <div class="mb-6">
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                        <div class="flex gap-2">
                                                                <input
                                                                        id="name"
                                                                        v-model="model.name"
                                                                        type="text"
                                                                        placeholder="Enter 2D model name"
                                                                        class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none"
                                                                >
                                                                <button
                                                                        type="button"
                                                                        :disabled="saving"
                                                                        class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50"
                                                                        @click="saveModel"
                                                                >
                                                                        {{ saving ? 'Saving...' : 'Save' }}
                                                                </button>
                                                        </div>
                                                </div>

                                                <!-- Image Section -->
                                                <div class="mb-6">
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">
                                                                Image
                                                        </label>
                                                        <div class="flex items-start gap-4">
                                                                <div class="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                                                                        <img
                                                                                v-if="model.imageUrl"
                                                                                :src="model.imageUrl"
                                                                                alt="Model image"
                                                                                class="w-full h-full object-cover"
                                                                        >
                                                                        <svg v-else class="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                        </svg>
                                                                </div>
                                                                <div class="flex flex-col gap-2">
                                                                        <button
                                                                                type="button"
                                                                                class="py-2 px-4 text-sm font-medium text-orange-7 bg-transparent border border-orange-4 rounded-lg cursor-pointer transition-colors hover:bg-orange-1"
                                                                                @click="uploadImage"
                                                                        >
                                                                                Upload Image
                                                                        </button>
                                                                        <button
                                                                                v-if="model.imageUrl"
                                                                                type="button"
                                                                                class="py-2 px-4 text-sm font-medium text-red-600 bg-transparent border border-red-200 rounded-lg cursor-pointer transition-colors hover:bg-red-50"
                                                                                @click="removeImage"
                                                                        >
                                                                                Remove Image
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>

                                                <!-- Stack On Top Toggle -->
                                                <div class="mb-6">
                                                        <div class="flex items-center gap-3">
                                                                <button
                                                                        type="button"
                                                                        role="switch"
                                                                        :aria-checked="model.stackOnTop === 1"
                                                                        class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
                                                                        :class="model.stackOnTop === 1 ? 'bg-orange-7' : 'bg-gray-300'"
                                                                        @click="model.stackOnTop = model.stackOnTop === 1 ? 0 : 1"
                                                                >
                                                                        <span
                                                                                class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out"
                                                                                :class="model.stackOnTop === 1 ? 'translate-x-5' : 'translate-x-0'"
                                                                        ></span>
                                                                </button>
                                                                <span class="text-sm font-medium text-gray-700">Stack On Top</span>
                                                        </div>
                                                </div>

                                                <!-- Image Dimensions Section -->
                                                <div class="mb-6 pt-6 border-t border-gray-200">
                                                        <h3 class="text-sm font-semibold text-gray-900 mb-4">Image Dimensions</h3>
                                                        <div class="grid grid-cols-3 gap-4">
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Width (px)</label>
                                                                        <input
                                                                                v-model.number="model.imageWidth"
                                                                                type="number"
                                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Height (px)</label>
                                                                        <input
                                                                                v-model.number="model.imageHeight"
                                                                                type="number"
                                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">PPI</label>
                                                                        <input
                                                                                v-model.number="model.ppi"
                                                                                type="number"
                                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                </div>
                                                        </div>
                                                </div>

                                                <!-- Frame Position Section -->
                                                <div class="mb-6 pt-6 border-t border-gray-200">
                                                        <h3 class="text-sm font-semibold text-gray-900 mb-4">Frame Position</h3>
                                                        <div class="grid grid-cols-2 gap-4">
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Left</label>
                                                                        <input
                                                                                v-model.number="model.frameLeft"
                                                                                type="number"
                                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Top</label>
                                                                        <input
                                                                                v-model.number="model.frameTop"
                                                                                type="number"
                                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                </div>
                                                        </div>
                                                </div>

                                                <!-- Frame Dimensions Section -->
                                                <div class="mb-6 pt-6 border-t border-gray-200">
                                                        <h3 class="text-sm font-semibold text-gray-900 mb-4">Frame Dimensions</h3>
                                                        <div class="grid grid-cols-2 gap-4">
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Width</label>
                                                                        <input
                                                                                v-model.number="model.frameWidth"
                                                                                type="number"
                                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-xs font-medium text-gray-500 mb-1">Height</label>
                                                                        <input
                                                                                v-model.number="model.frameHeight"
                                                                                type="number"
                                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                </div>
                                                        </div>
                                                </div>

                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Toast Notification -->
                <div
                        v-if="toastMessage"
                        class="toast-container fixed right-6 z-50 transition-all duration-300"
                        :class="toastMessage ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'"
                >
                        <div
                                class="flex items-center gap-3 py-3 px-4 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'"
                        >
                                <svg
                                        v-if="toastVariant === 'success'"
                                        class="w-5 h-5 text-green-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2"
                                >
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg
                                        v-else
                                        class="w-5 h-5 text-red-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2"
                                >
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <span
                                        class="text-sm font-medium"
                                        :class="toastVariant === 'success' ? 'text-green-800' : 'text-red-800'"
                                >
                                        {{ toastMessage }}
                                </span>
                                <button
                                        type="button"
                                        class="ml-2 p-1 rounded transition-colors"
                                        :class="toastVariant === 'success' ? 'text-green-500 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { PickerOptions, PickerResponse } from 'filestack-js';
import Model2DService from 'services/Model2DService';

export default defineComponent({
        name: 'EditModel2DPage',
        setup() {
                const router = useRouter();
                const route = useRoute();
                const instance = getCurrentInstance();

                const routeId = computed(() => route.params.id as string);
                const loading = ref(true);
                const saving = ref(false);
                const model = ref<any>({});
                const toastMessage = ref('');
                const toastVariant = ref<'success' | 'danger'>('success');

                const goBack = () => {
                        router.push('/model2d');
                };

                const loadModel = async () => {
                        loading.value = true;
                        try {
                                const data = await Model2DService.get(routeId.value);
                                model.value = data;
                        } catch (error) {
                                console.error('Failed to load model:', error);
                                toastMessage.value = 'Failed to load 2D model';
                                toastVariant.value = 'danger';
                        } finally {
                                loading.value = false;
                        }
                };

                const saveModel = async () => {
                        saving.value = true;
                        try {
                                await Model2DService.update(routeId.value, model.value);
                                toastMessage.value = '2D model saved successfully';
                                toastVariant.value = 'success';
                                setTimeout(() => {
                                        toastMessage.value = '';
                                }, 3000);
                        } catch (error) {
                                console.error('Failed to save model:', error);
                                toastMessage.value = 'Failed to save 2D model';
                                toastVariant.value = 'danger';
                        } finally {
                                saving.value = false;
                        }
                };

                const uploadImage = () => {
                        const filestackClient = instance?.appContext.config.globalProperties.$filestack;
                        if (!filestackClient) return;

                        const options: PickerOptions = {
                                accept: ['image/*'],
                                maxFiles: 1,
                                onUploadDone: (result: PickerResponse) => {
                                        if (result.filesUploaded.length > 0) {
                                                model.value.imageUrl = result.filesUploaded[0].url;
                                        }
                                },
                        };
                        filestackClient.picker(options).open();
                };

                const removeImage = () => {
                        model.value.imageUrl = '';
                };

                const hideToast = () => {
                        toastMessage.value = '';
                };

                onMounted(() => {
                        loadModel();
                });

                return {
                        routeId,
                        loading,
                        saving,
                        model,
                        toastMessage,
                        toastVariant,
                        saveModel,
                        uploadImage,
                        removeImage,
                        goBack,
                        hideToast,
                };
        },
});
</script>

<style>
.save-btn-desktop {
        display: none !important;
}

.save-btn-mobile {
        display: block !important;
}

.toast-container {
        bottom: 5rem !important;
}

@media (min-width: 768px) {
        .save-btn-desktop {
                display: inline-flex !important;
        }

        .save-btn-mobile {
                display: none !important;
        }

        .toast-container {
                bottom: 1.5rem !important;
        }
}
</style>
